import { useState } from "react";

type FormState = "idle" | "submitting" | "success" | "error";

export default function OverstockQuoteForm() {
  const [formState, setFormState] = useState<FormState>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const notes = (form.elements.namedItem("additionalNotes") as HTMLTextAreaElement)?.value || "";
    if (notes.length > 500) return;

    setFormState("submitting");

    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) {
        formData.append(el.name, el.value);
      }
    }

    try {
      const res = await fetch("https://readdy.ai/api/form/d71j5hc991ove783t0pg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      if (res.ok) {
        setFormState("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormState("error");
      }
    } catch {
      setFormState("error");
    }
  };

  return (
    <div className="mt-14 rounded-2xl bg-[#2C3A4F] border border-white/10 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-8 pt-8 pb-6 border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C9A227]/20 flex-shrink-0">
            <i className="ri-file-list-3-line text-2xl text-[#C9A227]"></i>
          </div>
          <div>
            <h3 className="text-white font-bold text-xl">Request a Custom Quote</h3>
            <p className="text-white/50 text-sm mt-0.5">
              Tell us what you need — we&apos;ll match you with the right overstock inventory fast.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/30 self-start lg:self-auto">
          <span className="w-2 h-2 rounded-full bg-[#C9A227] animate-pulse flex-shrink-0"></span>
          <span className="text-[#C9A227] text-xs font-semibold whitespace-nowrap">Response within 24 hrs</span>
        </div>
      </div>

      {/* Form Body */}
      <div className="px-8 py-8">
        {formState === "success" ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-500/20">
              <i className="ri-check-line text-green-400 text-3xl"></i>
            </div>
            <h4 className="text-white font-bold text-xl">Quote Request Received!</h4>
            <p className="text-white/50 text-sm max-w-sm">
              Our overstock team will review your requirements and get back to you within 24 hours with matching inventory options.
            </p>
            <button
              onClick={() => setFormState("idle")}
              className="mt-2 text-sm font-semibold text-[#C9A227] hover:underline cursor-pointer whitespace-nowrap"
            >
              Submit another request
            </button>
          </div>
        ) : (
          <form
            id="overstock-quote-form"
            data-readdy-form
            onSubmit={handleSubmit}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-white/50 uppercase tracking-wide">
                  Your Name *
                </label>
                <input
                  type="text"
                  name="contactName"
                  required
                  placeholder="Jane Doe"
                  className="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/15 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C9A227] transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-white/50 uppercase tracking-wide">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="jane@company.com"
                  className="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/15 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C9A227] transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-white/50 uppercase tracking-wide">
                  Company Name
                </label>
                <input
                  type="text"
                  name="companyName"
                  placeholder="Acme Wholesale"
                  className="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/15 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C9A227] transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-white/50 uppercase tracking-wide">
                  Product Type / Category *
                </label>
                <select
                  name="productType"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-[#222831] border border-white/15 text-sm text-white focus:outline-none focus:border-[#C9A227] transition-colors"
                >
                  <option value="">Select category</option>
                  <option>Lighting &amp; Electrical</option>
                  <option>Kitchenware &amp; Cookware</option>
                  <option>Safety &amp; PPE</option>
                  <option>Packaging &amp; Shipping</option>
                  <option>Cleaning Supplies</option>
                  <option>Electronics &amp; Gadgets</option>
                  <option>Apparel &amp; Textiles</option>
                  <option>Tools &amp; Hardware</option>
                  <option>Health &amp; Beauty</option>
                  <option>Other</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-white/50 uppercase tracking-wide">
                  Quantity Needed *
                </label>
                <select
                  name="quantityNeeded"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-[#222831] border border-white/15 text-sm text-white focus:outline-none focus:border-[#C9A227] transition-colors"
                >
                  <option value="">Select quantity range</option>
                  <option>Under 500 units</option>
                  <option>500 – 2,000 units</option>
                  <option>2,000 – 5,000 units</option>
                  <option>5,000 – 10,000 units</option>
                  <option>10,000 – 50,000 units</option>
                  <option>50,000+ units</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-white/50 uppercase tracking-wide">
                  Delivery Timeline *
                </label>
                <select
                  name="deliveryTimeline"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-[#222831] border border-white/15 text-sm text-white focus:outline-none focus:border-[#C9A227] transition-colors"
                >
                  <option value="">Select timeline</option>
                  <option>ASAP (within 1 week)</option>
                  <option>2 – 4 weeks</option>
                  <option>1 – 2 months</option>
                  <option>2 – 3 months</option>
                  <option>Flexible / No rush</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 mt-5">
              <label className="text-xs font-semibold text-white/50 uppercase tracking-wide flex justify-between">
                <span>Additional Notes</span>
                <span className={charCount > 450 ? "text-red-400" : "text-white/30"}>
                  {charCount}/500
                </span>
              </label>
              <textarea
                name="additionalNotes"
                rows={3}
                maxLength={500}
                placeholder="Describe specific product requirements, brands, conditions (new/refurbished), destination country, or any other details that help us find the right match..."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/15 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C9A227] transition-colors resize-none"
              />
            </div>

            {formState === "error" && (
              <p className="mt-4 text-red-400 text-sm">
                Something went wrong. Please try again or email us directly at info@ltfsourcing.com.
              </p>
            )}

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
              <p className="text-white/30 text-xs">
                We&apos;ll match your request against our live overstock inventory and reach out with options.
              </p>
              <button
                type="submit"
                disabled={formState === "submitting"}
                className="px-8 py-3.5 rounded-xl bg-[#C9A227] text-white font-bold text-sm hover:bg-[#b08f1e] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-60 flex-shrink-0"
              >
                {formState === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Sending Request...
                  </>
                ) : (
                  <>
                    Submit Quote Request
                    <i className="ri-send-plane-line text-base"></i>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
